import React, { useEffect } from 'react';
import { Box, Modal, SelectChangeEvent } from '@mui/material';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { InputSelect } from 'app/components/elements/InputSelect';
import { InputText } from 'app/components/elements/InputText';
import { CreateSingleDataEntryField } from 'services/option';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormSingleFieldPropsSchema } from 'utils/validate';

export const inspectionOptions = [
  { value: 'REPORT_ISSUE', label: 'Report Issue' },
  { value: 'ASSET', label: 'Asset' },
  { value: 'ADDITIONAL_STRUCTURE', label: 'Additional Structure' },
  { value: 'ROOM_LEVEL_ISSUE', label: 'Location Level Issue' },
  { value: 'ROOM', label: 'Room' }
];
export const fieldTypes = [
  { value: 'TEXT', label: 'Single text' },
  { value: 'DATE', label: 'Date' }
];

const normalizeMultiSelectValue = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (raw == null) return [];

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    // Support legacy values stored as JSON string.
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
        if (parsed && typeof parsed === 'object') {
          return Object.entries(parsed as Record<string, unknown>)
            .filter(([, v]) => Boolean(v))
            .map(([k]) => k);
        }
      } catch {
        // fall through to other strategies
      }
    }

    // Support comma separated legacy values.
    if (trimmed.includes(',')) {
      return trimmed
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    return [trimmed];
  }

  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>)
      .filter(([, v]) => Boolean(v))
      .map(([k]) => k);
  }

  return [String(raw)];
};

const ModalNewSingleField = ({
  defaultValue,
  open,
  onClose,
  onCreate,
  onUpdate,
  clientOptions
}: {
  defaultValue?: CreateSingleDataEntryField & { id: number };
  open: boolean;
  onClose: () => void;
  onCreate: (val: CreateSingleDataEntryField) => Promise<void>;
  onUpdate: (id: number, val: Partial<CreateSingleDataEntryField>) => Promise<void>;
  clientOptions?: { value: string; label: string }[];
}) => {
  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors }
  } = useForm<CreateSingleDataEntryField>({
    resolver: yupResolver(FormSingleFieldPropsSchema)
  });
  const [selectedClientIds, setSelectedClientIds] = React.useState<string[]>([]);

  useEffect(() => {
    reset({
      name: defaultValue?.name || '',
      type: defaultValue?.type || '',
      required: Boolean(defaultValue?.required),
      entries: normalizeMultiSelectValue(defaultValue?.entries)
    });
    if (defaultValue?.clientIds) {
      setSelectedClientIds(defaultValue.clientIds.map(String));
    } else {
      setSelectedClientIds([]);
    }
  }, [defaultValue, reset]);

  const onSave = async (formData: CreateSingleDataEntryField) => {
    onClose();
    const clientIds = selectedClientIds.length ? selectedClientIds.map(Number) : undefined;
    if (defaultValue?.id) {
      await onUpdate(defaultValue.id, { ...formData, clientIds });
      return;
    }
    await onCreate({ ...formData, clientIds });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          maxWidth: 400,
          margin: 'auto',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Box
          sx={{
            padding: 2,
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderRadius: '10px',
            overflow: 'hidden',
            flex: 1,
            paddingBottom: 2
          }}>
          <h2 style={{ margin: 0 }}>{defaultValue?.id ? 'Edit' : 'Create'} Single Entry Field</h2>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                title={'Select Entries'}
                inputError={errors.entries?.message || ''}
                multiple
                value={Array.isArray(value) ? value : normalizeMultiSelectValue(value)}
                placeholder={'Select Entries'}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  onChange(normalizeMultiSelectValue(e.target.value), { shouldDirty: true });
                }}
                options={inspectionOptions}
              />
            )}
            name={'entries'}
          />
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                title={'Select Type'}
                inputError={errors.type?.message || ''}
                value={value}
                placeholder={'Select Type'}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  onChange(e.target.value, { shouldDirty: true });
                }}
                options={fieldTypes}
              />
            )}
            name={`type`}
          />
          <InputText
            title={'Single Field Name'}
            placeholder={'Enter Single Field name'}
            inputProps={{
              ...register('name')
            }}
          />
          {clientOptions && (
            <InputSelect
              multiple
              options={clientOptions}
              value={selectedClientIds}
              title="Clients"
              placeholder="Select clients"
              onChange={(e) => {
                setSelectedClientIds(e.target.value as unknown as string[]);
              }}
            />
          )}
          <ButtonCustom label="Save" onClick={handleSubmit(onSave)} />
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalNewSingleField;
